import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChevronDown, Plus } from "lucide-react";
import API from "../services/api";
import MarketSheet from "../components/MarketSheet";
import { getSuggestedSession } from "../utils/marketStatus";


const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT" },
  { id: 2, name: "MADHUR NIGHT" },
  { id: 3, name: "MILAN NIGHT" },
  { id: 4, name: "ANMOL BAZAR" },
  { id: 5, name: "KALYAN NIGHT" },
  { id: 6, name: "RAJDHANI NIGHT" },
  { id: 7, name: "MAIN BAZAR" },
  { id: 8, name: "VANDE BHARAT" },
  { id: 9, name: "KALYAN" },
  { id: 10, name: "RAJDHANI DAY" },
  { id: 11, name: "MILAN DAY" },
  { id: 12, name: "SADAR BAZAR" },
  { id: 13, name: "MADHUR DAY" },
  { id: 14, name: "TIME BAZAR" },
  { id: 15, name: "SRIDEVI" },
  { id: 16, name: "MILAN MORNING" },
  { id: 17, name: "KANTARA" },
];

const themes = {
  violet: {
    headerLine: "linear-gradient(90deg, #059669, #10b981, #34d399)",
    pillBorder: "#6ee7b7",
    pillBg: "linear-gradient(90deg, #ecfdf5, #f0fdf4)",
    pillText: "#065f46",
    selectorBg: "linear-gradient(135deg, #ecfdf5, #f0fdf4)",
    selectorBorder: "#a7f3d0",
    label: "#065f46",
    selectBorder: "#6ee7b7",
    accent: "#059669",
    total: "#059669",
    primary: "linear-gradient(90deg, #34d399, #10b981)",
  },
  orange: {
    headerLine: "linear-gradient(90deg, #f59e0b, #f97316, #ef4444)",
    pillBorder: "#fcd34d",
    pillBg: "linear-gradient(90deg, #fffbeb, #fef9c3)",
    pillText: "#92400e",
    selectorBg: "linear-gradient(135deg, #fffbeb, #fef9c3)",
    selectorBorder: "#fde68a",
    label: "#92400e",
    selectBorder: "#fcd34d",
    accent: "#d97706",
    total: "#d97706",
    primary: "linear-gradient(90deg, #fcd34d, #fbbf24)",
  },
};

const DIGIT_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

const variants = {
  single_bulk: {
    title: "SINGLE BULK",
    theme: "violet",
    mode: "digitBulk",
    numbers: DIGIT_OPTIONS,
    amountLabel: "Enter Amount",
    typePrefix: "single_bulk",
  },
  jodi: {
    title: "JODI",
    theme: "violet",
    mode: "pairList",
    numbers: Array.from({ length: 100 }, (_, index) => String(index).padStart(2, "0")),
    amountLabel: "Enter Amount",
    typePrefix: "jodi",
    forceSession: "CLOSE",
    numberLabel: "Jodi Number",
    placeholder: "00",
  },
  jodi_bulk: {
    title: "JODI BULK",
    theme: "violet",
    mode: "pairBulk",
    numbers: Array.from({ length: 100 }, (_, index) => String(index).padStart(2, "0")),
    amountLabel: "Enter Amount",
    typePrefix: "jodi_bulk",
    forceSession: "CLOSE",
  },
  red_bracket: {
    title: "RED BRACKET",
    theme: "orange",
    mode: "redBracketSet",
    numbers: ["00", "05", "11", "16", "22", "27", "33", "38", "44", "49", "50", "55", "61", "66", "72", "77", "83", "88", "94", "99"],
    amountLabel: "Enter Amount",
    typePrefix: "red_bracket",
    forceSession: "CLOSE",
  },
  sp: {
    title: "SINGLE PATTI",
    theme: "violet",
    mode: "pattiList",
    pattiLabel: "Enter Patti",
    addLabel: "Add Bid to List",
    typePrefix: "sp",
  },
  sp_bulk: {
    title: "SP BULK",
    theme: "violet",
    mode: "baseBulk",
    numbers: DIGIT_OPTIONS,
    amountLabel: "Enter Amount",
    helper: "Select Base (12 SP numbers each)",
    typePrefix: "sp_bulk",
  },
  sp_motor: {
    title: "SP MOTOR",
    theme: "violet",
    mode: "motor",
    digitsLabel: "SP Motor Digits",
    digitsHelper: "(Min 4 unique digits)",
    amountLabel: "Bid Amount (per number)",
    generatedLabel: "Single Patti Bids",
    minDigits: 4,
    typePrefix: "sp_motor",
  },
  dp: {
    title: "DOUBLE PATTI",
    theme: "orange",
    mode: "pattiList",
    pattiLabel: "Enter Patti",
    addLabel: "Add Bid to List",
    typePrefix: "dp",
  },
  dp_bulk: {
    title: "DP BULK",
    theme: "orange",
    mode: "baseBulk",
    numbers: DIGIT_OPTIONS,
    amountLabel: "Enter Amount",
    helper: "Select Base (9 DP numbers each)",
    typePrefix: "dp_bulk",
  },
  dp_motor: {
    title: "DP MOTOR",
    theme: "orange",
    mode: "motor",
    digitsLabel: "DP Motor Digits",
    digitsHelper: "(Min 4 unique digits)",
    amountLabel: "Bid Amount (per number)",
    generatedLabel: "Double Patti Bids",
    minDigits: 4,
    typePrefix: "dp_motor",
  },
  tp: {
    title: "TRIPLE PATTI",
    theme: "orange",
    mode: "tripleAmountGrid",
    numbers: ["000", "111", "222", "333", "444", "555", "666", "777", "888", "999"],
    amountLabel: "Enter Amount",
    typePrefix: "tp",
  },
  half_sangam: {
    title: "HALF SANGAM",
    theme: "violet",
    mode: "halfSangam",
    typePrefix: "half_sangam",
    forceSession: "CLOSE",
  },
  full_sangam: {
    title: "FULL SANGAM",
    theme: "violet",
    mode: "fullSangam",
    typePrefix: "full_sangam",
    forceSession: "CLOSE",
  },
};

function GameBetPage({ variant }) {
  const config = variants[variant] || variants.single_bulk;
  const theme = themes[config.theme];
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(sessionStorage.getItem("selectedMarketId") || "1");
  const [session, setSession] = useState("CLOSE");
  const [pointAmount, setPointAmount] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [gridAmounts, setGridAmounts] = useState({});
  const [patti, setPatti] = useState("");
  const [pattiError, setPattiError] = useState("");
  const [entryAmount, setEntryAmount] = useState("");
  const [entries, setEntries] = useState([]);
  const [motorDigits, setMotorDigits] = useState("");
  const [motorAmount, setMotorAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [limits, setLimits] = useState({ min_bet: "10", max_bet: "10000" });
  const [ankStatus, setAnkStatus] = useState({ ank_limit: null, used_digits: [], used_count: 0, remaining: null });
  const [marketSheetOpen, setMarketSheetOpen] = useState(false);
  const [sangamErrors, setSangamErrors] = useState({});
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sangam, setSangam] = useState({
    openPana: "",
    closeDigit: "",
    openDigit: "",
    closePana: "",
    closePanaFull: "",
  });
  const [betSuccess, setBetSuccess] = useState(null);
  const [sangamMode, setSangamMode] = useState("open");
  const placingRef = useRef(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    API.get("/markets")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const normalRows = rows.filter((market) => (market.market_type || "normal") !== "starline");
        setMarkets(normalRows.length ? normalRows : fallbackMarkets);
      })
      .catch(() => setMarkets(fallbackMarkets));
    API.get("/settings/public")
      .then((res) => setLimits((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const targetSession = config.forceSession || session;
    if (!selectedMarket || !targetSession) return;
    API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(targetSession)}`)
      .then((res) => setAnkStatus(res.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null }))
      .catch(() => {});
  }, [config.forceSession, selectedMarket, session, user?.id]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const marketOptions = markets.length ? markets : fallbackMarkets;
  const selectedMarketInfo = marketOptions.find((market) => String(market.id) === String(selectedMarket)) || marketOptions[0];
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" }).toUpperCase();
  const amountValue = Number(pointAmount || 0);

  const generatedMotorEntries = useMemo(() => {
    const uniqueDigits = Array.from(new Set((motorDigits || "").replace(/\D/g, "").split(""))).sort();
    if (config.mode !== "motor" || uniqueDigits.length < (config.minDigits || 0)) return [];

    if (variant === "sp_motor") {
      return SINGLE_PATTI_OPTIONS.filter((option) =>
        option.split("").every((digit) => uniqueDigits.includes(digit))
      );
    }

    if (variant === "dp_motor") {
      return DOUBLE_PATTI_OPTIONS.filter((option) =>
        new Set(option.split("")).size === 2 &&
        Array.from(new Set(option.split(""))).every((digit) => uniqueDigits.includes(digit))
      );
    }

    return [];
  }, [config.minDigits, config.mode, motorDigits, variant]);

  const total = useMemo(() => {
    if (config.typePrefix === "sp_bulk") {
      return selectedNumbers.reduce((sum, n) => sum + getSinglePattisForDigit(n).length * Number(pointAmount || 0), 0);
    }

    if (config.typePrefix === "dp_bulk") {
      return selectedNumbers.reduce((sum, n) => sum + getDoublePattisForDigit(n).length * Number(pointAmount || 0), 0);
    }

    if (["digitBulk", "baseBulk", "pairBulk", "redBracketSet"].includes(config.mode)) {
      return selectedNumbers.length * Number(pointAmount || 0);
    }

    if (config.mode === "pairAmountGrid") {
      return Object.values(gridAmounts).reduce((sum, value) => sum + Number(value || 0), 0);
    }

    if (config.mode === "tripleAmountGrid") {
      return Object.values(gridAmounts).reduce((sum, value) => sum + Number(value || 0), 0);
    }

    return entries.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [config.mode, config.typePrefix, entries, gridAmounts, pointAmount, selectedNumbers]);
  const draftEntries = useMemo(() => {
    const amount = Number(entryAmount || 0);
    if (amount <= 0) return [];

    if (["pattiList", "pairList"].includes(config.mode)) {
      const requiredLength = config.mode === "pairList" ? 2 : 3;
      if (!new RegExp(`^\\d{${requiredLength}}$`).test(patti)) return [];
      if (config.mode === "pattiList" && validatePattiValue(config.typePrefix, patti)) return [];
      return [{ number: patti, amount }];
    }

    if (config.mode === "halfSangam") {
      const openCombo = /^\d{3}$/.test(sangam.openPana) && /^\d$/.test(sangam.closeDigit);
      const closeCombo = /^\d$/.test(sangam.openDigit) && /^\d{3}$/.test(sangam.closePana);
      if (openCombo && !validatePannaOption(sangam.openPana)) {
        return [{ number: `${sangam.openPana}-${sangam.closeDigit}`, amount }];
      }
      if (closeCombo && !validatePannaOption(sangam.closePana)) {
        return [{ number: `${sangam.openDigit}-${sangam.closePana}`, amount }];
      }
      return [];
    }

    if (config.mode === "fullSangam") {
      if (/^\d{3}$/.test(sangam.openPana) && /^\d{3}$/.test(sangam.closePanaFull) && !validatePannaOption(sangam.openPana) && !validatePannaOption(sangam.closePanaFull)) {
        return [{ number: `${sangam.openPana}-${sangam.closePanaFull}`, amount }];
      }
      return [];
    }

    if (config.mode === "motor" && generatedMotorEntries.length && Number(motorAmount || 0) > 0) {
      return generatedMotorEntries.map((number) => ({ number, amount: Number(motorAmount) }));
    }

    return [];
  }, [config.mode, config.typePrefix, entryAmount, generatedMotorEntries, motorAmount, patti, sangam.closeDigit, sangam.closePana, sangam.closePanaFull, sangam.openDigit, sangam.openPana]);
  const effectiveTotal = total + draftEntries.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const pattiDigit = useMemo(() => {
    const digits = String(patti || "").replace(/\D/g, "").slice(0, 3).split("").map(Number);
    if (!digits.length) return "";
    return String(digits.reduce((sum, digit) => sum + digit, 0) % 10);
  }, [patti]);
  const validPattiOptions = useMemo(() => {
    if (config.typePrefix === "sp") return SINGLE_PATTI_OPTIONS;
    if (config.typePrefix === "dp") return DOUBLE_PATTI_OPTIONS;
    return [];
  }, [config.typePrefix]);
  const pattiSuggestions = useMemo(() => {
    if (!patti || !validPattiOptions.length) return [];
    return validPattiOptions.filter((option) => option.startsWith(patti)).slice(0, 24);
  }, [patti, validPattiOptions]);
  const showPattiNoResult = useMemo(() => {
    if (!patti || !validPattiOptions.length) return false;
    return pattiSuggestions.length === 0;
  }, [patti, pattiSuggestions.length, validPattiOptions.length]);
  const bulkPreviewEntries = useMemo(() => {
    if (!selectedNumbers.length) return [];
    if (config.typePrefix === "sp_bulk") {
      return selectedNumbers.flatMap((number) =>
        getSinglePattisForDigit(number).map((pana) => ({ number: pana, amount: amountValue }))
      );
    }
    if (config.typePrefix === "dp_bulk") {
      return selectedNumbers.flatMap((number) =>
        getDoublePattisForDigit(number).map((pana) => ({ number: pana, amount: amountValue }))
      );
    }
    return [];
  }, [amountValue, config.typePrefix, selectedNumbers]);
  const sangamSuggestions = useMemo(() => ({
    openPana: getPannaSuggestions(sangam.openPana),
    closePana: getPannaSuggestions(sangam.closePana),
    closePanaFull: getPannaSuggestions(sangam.closePanaFull),
  }), [sangam.closePana, sangam.closePanaFull, sangam.openPana]);
  const halfSangamOpenSelected = Boolean(sangam.openPana || sangam.closeDigit);
  const halfSangamCloseSelected = Boolean(sangam.openDigit || sangam.closePana);
  const bulkNeedsAmountFirst = ["sp_bulk", "dp_bulk"].includes(config.typePrefix);
  const isCompact = viewportWidth <= 430;
  const isTiny = viewportWidth <= 360;
  const isDesktop = viewportWidth >= 900;
  const isAnkMode = config.typePrefix === "single_bulk";
  const usedDigitSet = useMemo(() => new Set((ankStatus.used_digits || []).map((value) => String(value))), [ankStatus.used_digits]);
  const ankLimitReached = false;
  const getSelectedNewAnkCount = (nextSelected = selectedNumbers) =>
    Array.from(new Set(nextSelected.map((value) => String(value))))
      .filter((value) => !usedDigitSet.has(value)).length;
  const isDigitOptionDisabled = (value) => {
    if (!isAnkMode || selectedNumbers.includes(value)) return false;
    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    return limit > 0 && !usedDigitSet.has(String(value)) && getSelectedNewAnkCount() >= remaining;
  };

  const handleMarketChange = (value) => {
    sessionStorage.setItem("selectedMarketId", String(value));
    setSelectedMarket(value);
  };

  useEffect(() => {
    if (config.forceSession || !selectedMarketInfo) return;
    setSession(getSuggestedSession(selectedMarketInfo));
  }, [config.forceSession, selectedMarketInfo]);

  const toggleNumber = (value) => {
    if (isDigitOptionDisabled(value)) {
      alert(`Ank limit reached. You can use only ${ankStatus.ank_limit} digits in this market and session.`);
      return;
    }
    if (bulkNeedsAmountFirst && Number(pointAmount || 0) <= 0) {
      alert("Enter amount first");
      return;
    }
    setSelectedNumbers((current) => {
      if (current.includes(value)) return current.filter((item) => item !== value);
      const next = [...current, value];
      const limit = Number(ankStatus.ank_limit || 0);
      const remaining = Number(ankStatus.remaining || 0);
      if (isAnkMode && limit > 0 && !usedDigitSet.has(String(value)) && getSelectedNewAnkCount(next) > remaining) {
        alert(`Ank limit reached. You can use only ${ankStatus.ank_limit} digits in this market and session.`);
        return current;
      }
      return next;
    });
  };

  const handleGridAmount = (number, value) => {
    setGridAmounts((current) => ({
      ...current,
      [number]: value.replace(/[^\d]/g, ""),
    }));
  };

  const addManualEntry = () => {
    const requiredLength = config.mode === "pairList" ? 2 : 3;
    if (!new RegExp(`^\\d{${requiredLength}}$`).test(patti) || Number(entryAmount) <= 0) {
      setPattiError("");
      alert(`Enter a valid ${requiredLength}-digit number and amount`);
      return;
    }

    const pattiError = validatePattiValue(config.typePrefix, patti);
    if (pattiError) {
      setPattiError(pattiError);
      return;
    }

    setPattiError("");
    setEntries((current) => [...current, { number: patti, amount: Number(entryAmount) }]);
    setPatti("");
    setEntryAmount("");
  };

  const updateSangam = (key, value, maxLength) => {
    const nextValue = value.replace(/\D/g, "").slice(0, maxLength);
    setSangam((current) => ({ ...current, [key]: nextValue }));
    setSangamErrors((current) => ({
      ...current,
      [key]: maxLength === 3 && nextValue.length === 3 ? validatePannaOption(nextValue) : "",
    }));
  };

  const addHalfSangamEntry = () => {
    const amount = Number(entryAmount || 0);
    const openCombo = /^\d{3}$/.test(sangam.openPana) && /^\d$/.test(sangam.closeDigit);
    const closeCombo = /^\d$/.test(sangam.openDigit) && /^\d{3}$/.test(sangam.closePana);
    const openPanaError = sangam.openPana.length === 3 ? validatePannaOption(sangam.openPana) : "";
    const closePanaError = sangam.closePana.length === 3 ? validatePannaOption(sangam.closePana) : "";
    if (openPanaError || closePanaError) {
      setSangamErrors((current) => ({ ...current, openPana: openPanaError, closePana: closePanaError }));
      return;
    }
    if ((!openCombo && !closeCombo) || amount <= 0) {
      alert("Enter one Half Sangam combination and amount");
      return;
    }
    const number = openCombo ? `${sangam.openPana}-${sangam.closeDigit}` : `${sangam.openDigit}-${sangam.closePana}`;
    setEntries((current) => [...current, { number, amount }]);
    setSangamErrors({});
    setSangam((current) => ({ ...current, openPana: "", closeDigit: "", openDigit: "", closePana: "" }));
    setEntryAmount("");
  };

  const addFullSangamEntry = () => {
    const amount = Number(entryAmount || 0);
    const openPanaError = sangam.openPana.length === 3 ? validatePannaOption(sangam.openPana) : "";
    const closePanaError = sangam.closePanaFull.length === 3 ? validatePannaOption(sangam.closePanaFull) : "";
    if (openPanaError || closePanaError) {
      setSangamErrors((current) => ({ ...current, openPana: openPanaError, closePanaFull: closePanaError }));
      return;
    }
    if (!/^\d{3}$/.test(sangam.openPana) || !/^\d{3}$/.test(sangam.closePanaFull) || amount <= 0) {
      alert("Enter open panna, close panna and amount");
      return;
    }
    setEntries((current) => [...current, { number: `${sangam.openPana}-${sangam.closePanaFull}`, amount }]);
    setSangamErrors({});
    setSangam((current) => ({ ...current, openPana: "", closePanaFull: "" }));
    setEntryAmount("");
  };

  const addMotorEntries = () => {
    if (!generatedMotorEntries.length || Number(motorAmount) <= 0) {
      alert("Enter valid digits and amount");
      return;
    }

    setEntries((current) => [
      ...current,
      ...generatedMotorEntries.map((number) => ({ number, amount: Number(motorAmount) })),
    ]);
    setMotorDigits("");
    setMotorAmount("");
  };

  const placeBets = async () => {
    if (placingRef.current || placing) return;
    if (!user?.id) {
      navigate("/login");
      return;
    }

    let payload = [];
    const minBet = Number(limits.min_bet || 10);
    const maxBet = Number(limits.max_bet || 10000);

    if (["digitBulk", "baseBulk", "pairBulk", "redBracketSet"].includes(config.mode)) {
      if (!selectedNumbers.length || amountValue <= 0) {
        alert("Select numbers and enter amount");
        return;
      }

      if (isAnkMode) {
        const limit = Number(ankStatus.ank_limit || 0);
        const remaining = Number(ankStatus.remaining || 0);
        if (limit > 0 && getSelectedNewAnkCount() > remaining) {
          alert(`Ank limit is ${limit} for this market and ${(config.forceSession || session)} session. Remove extra ank before submit.`);
          return;
        }
      }

      if (amountValue < minBet || amountValue > maxBet) {
        alert(`Each bet must be between Rs ${minBet} and Rs ${maxBet}`);
        return;
      }

      if (config.typePrefix === "sp_bulk") {
        payload = selectedNumbers.flatMap((number) =>
          getSinglePattisForDigit(number).map((pattiNumber) => ({
            user_id: user.id,
            market_id: selectedMarket,
            number: pattiNumber,
            amount: amountValue,
            type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
          }))
        );
      } else if (config.typePrefix === "dp_bulk") {
        payload = selectedNumbers.flatMap((number) =>
          getDoublePattisForDigit(number).map((pattiNumber) => ({
            user_id: user.id,
            market_id: selectedMarket,
            number: pattiNumber,
            amount: amountValue,
            type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
          }))
        );
      } else {
        payload = selectedNumbers.map((number) => ({
          user_id: user.id,
          market_id: selectedMarket,
          number,
          amount: amountValue,
          type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
        }));
      }
    } else if (config.mode === "pairAmountGrid" || config.mode === "tripleAmountGrid") {
      const selectedEntries = Object.entries(gridAmounts).filter(([, value]) => Number(value) > 0);
      if (!selectedEntries.length) {
        alert(config.mode === "tripleAmountGrid" ? "Enter amount for at least one triple patti" : "Enter amount for at least one jodi");
        return;
      }

      const invalidEntry = selectedEntries.find(([, value]) => Number(value || 0) < minBet || Number(value || 0) > maxBet);
      if (invalidEntry) {
        alert(`Each bet must be between Rs ${minBet} and Rs ${maxBet}`);
        return;
      }

      payload = selectedEntries.map(([number, value]) => ({
        user_id: user.id,
        market_id: selectedMarket,
        number,
        amount: Number(value),
        type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
      }));
    } else {
      const finalEntries = [...entries, ...draftEntries];
      if (!finalEntries.length) {
        alert("Add at least one bid to the list");
        return;
      }

      const invalidEntry = finalEntries.find((entry) => Number(entry.amount || 0) < minBet || Number(entry.amount || 0) > maxBet);
      if (invalidEntry) {
        alert(`Each bet must be between Rs ${minBet} and Rs ${maxBet}`);
        return;
      }

      payload = finalEntries.map((entry) => ({
        user_id: user.id,
        market_id: selectedMarket,
        number: entry.number,
        amount: Number(entry.amount),
        type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
      }));
    }

    const uniquePayload = [];
    const seenBets = new Set();
    for (const item of payload) {
      const key = `${item.market_id}|${item.number}|${item.type}|${item.time_slot || ""}`;
      if (seenBets.has(key)) continue;
      seenBets.add(key);
      uniquePayload.push(item);
    }

    placingRef.current = true;
    setPlacing(true);
    try {
      for (const item of uniquePayload) {
        await API.post("/place-bet", item);
      }
      if (user?.id) {
        const targetSession = config.forceSession || session;
        const statusRes = await API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(targetSession)}`);
        setAnkStatus(statusRes.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null });
      }
      setEntries([]);
      setSelectedNumbers([]);
      setGridAmounts({});
      setPatti("");
      setEntryAmount("");
      setMotorDigits("");
      setMotorAmount("");
      setSangam({ openPana: "", closeDigit: "", openDigit: "", closePana: "", closePanaFull: "" });
      setSangamErrors({});
      setPattiError("");
      setPointAmount("");
      setBetSuccess({ market: selectedMarketInfo?.name || "", total: effectiveTotal });
      setTimeout(() => { setBetSuccess(null); navigate("/game-select"); }, 2800);
    } catch (err) {
      alert(err?.response?.data?.message || "Error placing bet");
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  };

  return (
    <div style={styles.page}>
      <header style={styles.topBar}>
        <button type="button" style={styles.circleBack} onClick={() => navigate(-1)}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <h1 style={styles.title}>{selectedMarketInfo?.name || ""} - {config.title}</h1>
      </header>

      <main style={{ ...styles.main, padding: isCompact ? "12px 12px 0" : styles.main.padding }}>
        {/* Date + Session row */}
        <div style={styles.topRowFields}>
          <div style={styles.outlineField}>
            <CalendarDays size={15} color="#94a3b8" />
            <span style={styles.fieldText}>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
          </div>
          {config.forceSession ? (
            <div style={styles.outlineField}>
              <span style={styles.fieldText}>{config.forceSession}</span>
            </div>
          ) : (
            <div style={styles.outlineFieldSelect}>
              <select value={session} onChange={(e) => setSession(e.target.value)} style={styles.sessionSelect}>
                <option value="OPEN">Open</option>
                <option value="CLOSE">Close</option>
              </select>
              <ChevronDown size={15} color="#94a3b8" style={{ pointerEvents: "none", position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }} />
            </div>
          )}
        </div>
        {(["digitBulk", "baseBulk", "pairBulk", "redBracketSet"].includes(config.mode)) && (
          <>
            <section style={styles.whiteCard}>
              <label style={styles.secondaryLabel}>{config.amountLabel}</label>
              <div style={styles.largeInputWrap}>
                <input
                  value={pointAmount}
                  onChange={(event) => setPointAmount(event.target.value.replace(/[^\d]/g, ""))}
                  inputMode="numeric"
                  placeholder="₹"
                  style={styles.largeInput}
                />
              </div>
            </section>

            <section style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, marginTop: 12, boxSizing: "border-box" }}>
              {config.mode === "redBracketSet" && (
                <button
                  type="button"
                  style={styles.selectAllRed}
                  onClick={() => {
                    const allSelected = selectedNumbers.length === config.numbers.length;
                    setSelectedNumbers(allSelected ? [] : [...config.numbers]);
                  }}
                >
                  <span style={selectedNumbers.length === config.numbers.length ? styles.selectAllTickActive : styles.selectAllTick} />
                  <span>Select All Red Bracket (20 Jodis)</span>
                </button>
              )}
              {config.helper && <div style={{ ...styles.bulkTitle, color: theme.label }}>{config.helper}</div>}
              {bulkNeedsAmountFirst && Number(pointAmount || 0) <= 0 && (
                <div style={styles.noResultText}>Enter amount first, then select a number</div>
              )}
              {config.mode === "redBracketSet" ? (
                <div style={{ ...styles.redBracketGrid, gridTemplateColumns: isDesktop ? "repeat(5, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: isCompact ? 6 : styles.redBracketGrid.gap }}>
                  {config.numbers.map((number) => {
                    const active = selectedNumbers.includes(number);
                    const disabled = isDigitOptionDisabled(number);
                    return (
                      <button key={number} type="button" disabled={disabled} style={{ ...styles.numberButton, borderColor: active ? "#312e81" : "#e5e7eb", background: disabled ? "#e5e7eb" : active ? "#312e81" : "#fff", color: disabled ? "#94a3b8" : active ? "#fff" : "#374151", boxShadow: active ? "0 4px 14px rgba(49,46,129,0.35)" : "none", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.8 : 1 }} onClick={() => toggleNumber(number)}>{number}</button>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: config.mode === "digitBulk" ? "repeat(3, minmax(0,1fr))" : "repeat(5, minmax(0,1fr))",
                  gap: isCompact ? 8 : 10,
                }}>
                  {config.numbers.map((number) => {
                    const active = selectedNumbers.includes(number);
                    const disabled = (bulkNeedsAmountFirst && Number(pointAmount || 0) <= 0) || isDigitOptionDisabled(number);
                    const isDigitBulk = config.mode === "digitBulk";
                    const pattiCount = config.typePrefix === "sp_bulk"
                      ? getSinglePattisForDigit(number).length
                      : config.typePrefix === "dp_bulk"
                        ? getDoublePattisForDigit(number).length
                        : null;
                    return (
                      <button
                        key={number}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleNumber(number)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 3,
                          height: isDigitBulk ? (isCompact ? 72 : 84) : (isCompact ? 52 : 60),
                          border: active ? "2px solid #312e81" : "1.5px solid #e5e7eb",
                          borderRadius: isDigitBulk ? 18 : 12,
                          background: disabled ? "#f8fafc" : active ? "#312e81" : "#ffffff",
                          boxShadow: active ? "0 6px 18px rgba(49,46,129,0.28)" : "0 1px 3px rgba(0,0,0,0.06)",
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.4 : 1,
                        }}
                      >
                        <span style={{
                          fontSize: isDigitBulk ? (isCompact ? 24 : 28) : (isCompact ? 16 : 18),
                          fontWeight: 700,
                          color: disabled ? "#cbd5e1" : active ? "#ffffff" : "#6b7280",
                          lineHeight: 1,
                        }}>
                          {number}
                        </span>
                        {pattiCount !== null && (
                          <span style={{ fontSize: 9, fontWeight: 600, color: disabled ? "#e2e8f0" : active ? "rgba(255,255,255,0.65)" : "#94a3b8", lineHeight: 1 }}>
                            {pattiCount} pattis
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            {!!bulkPreviewEntries.length && (
              <div style={{ marginTop: 12, background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 12, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 11, fontWeight: 700, color: "#4f46e5" }}>
                  <span>Selected Panas ({bulkPreviewEntries.length})</span>
                  <span style={{ color: "#94a3b8" }}>Tap × to remove</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 6 }}>
                  {bulkPreviewEntries.map((entry) => (
                    <div key={`${entry.number}-${entry.amount}`} style={{ position: "relative", background: "#fff", border: "1px solid #e0e7ff", borderRadius: 9, padding: "6px 4px", textAlign: "center" }}>
                      <button type="button" style={{ position: "absolute", top: 2, right: 3, width: 14, height: 14, border: 0, borderRadius: "50%", background: "#fee2e2", color: "#dc2626", fontSize: 10, fontWeight: 900, display: "grid", placeItems: "center", cursor: "pointer", padding: 0 }}
                        onClick={() => {
                          const selectedBase = config.typePrefix === "sp_bulk"
                            ? selectedNumbers.find((n) => getSinglePattisForDigit(n).includes(entry.number))
                            : selectedNumbers.find((n) => getDoublePattisForDigit(n).includes(entry.number));
                          if (selectedBase) setSelectedNumbers((c) => c.filter((item) => item !== selectedBase));
                        }}>×</button>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#1e1b4b" }}>{entry.number}</div>
                      <div style={{ fontSize: 10, color: "#4f46e5", fontWeight: 700 }}>{amountValue > 0 ? `₹${entry.amount}` : "₹0"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {config.mode === "pairAmountGrid" && (
          <section style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "12px 10px", boxSizing: "border-box" }}>
            <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(10, minmax(0, 1fr))" : isCompact ? "repeat(4, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))", gap: isCompact ? 8 : 12 }}>
              {config.numbers.map((number) => (
                <div key={number} style={{ display: "grid", gap: 5 }}>
                  <div style={{ height: 34, borderRadius: 8, background: "#eef2ff", border: "1px solid #c7d2fe", display: "grid", placeItems: "center", color: "#312e81", fontSize: 13, fontWeight: 800 }}>{number}</div>
                  <input
                    value={gridAmounts[number] || ""}
                    onChange={(event) => handleGridAmount(number, event.target.value)}
                    inputMode="numeric"
                    placeholder="₹"
                    style={{ width: "100%", height: 38, minWidth: 0, borderRadius: 9, border: "1.5px solid #d1d5db", background: "#fff", color: "#111827", textAlign: "center", fontSize: 12, fontWeight: 700, outline: "none", boxSizing: "border-box" }}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {config.mode === "tripleAmountGrid" && (
          <section style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "12px 10px", boxSizing: "border-box" }}>
            <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(10, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))", gap: isCompact ? 6 : 10 }}>
              {config.numbers.map((number) => (
                <div key={number} style={{ display: "grid", gap: 5 }}>
                  <div style={{ height: 36, borderRadius: 8, background: "#f0fdf4", border: "1px solid #bbf7d0", display: "grid", placeItems: "center", color: "#15803d", fontSize: 13, fontWeight: 800 }}>{number}</div>
                  <input
                    value={gridAmounts[number] || ""}
                    onChange={(event) => handleGridAmount(number, event.target.value)}
                    inputMode="numeric"
                    placeholder="₹"
                    style={{ width: "100%", height: 38, minWidth: 0, borderRadius: 9, border: "1.5px solid #d1d5db", background: "#fff", color: "#111827", textAlign: "center", fontSize: 12, fontWeight: 700, outline: "none", boxSizing: "border-box" }}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {["pattiList", "pairList"].includes(config.mode) && (
          <section style={cleanCard}>
            <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div>
                <div style={cleanLabel}>{config.pattiLabel || config.numberLabel}</div>
                <input value={patti} onChange={(event) => { const v = event.target.value.replace(/[^\d]/g, "").slice(0, config.mode === "pairList" ? 2 : 3); setPatti(v); setPattiError(v.length === 3 ? validatePattiValue(config.typePrefix, v) : ""); }} inputMode="numeric" placeholder={config.placeholder || "000"} style={cleanInput} />
                {config.mode === "pattiList" && pattiDigit && <div style={autoDigitBadge}>Digit: {pattiDigit}</div>}
                {!!pattiError && <div style={errText}>{pattiError}</div>}
              </div>
              <div>
                <div style={cleanLabel}>Enter Points</div>
                <input value={entryAmount} onChange={(e) => setEntryAmount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="₹5-₹10000" style={cleanInput} />
              </div>
            </div>
            <button type="button" style={addBidBtn} onClick={addManualEntry}><Plus size={18} /> ADD BID</button>
            {!!entries.length && (
              <div style={entryListWrap}>
                {entries.map((entry, i) => (
                  <div key={`${entry.number}-${i}`} style={entryListRow}>
                    <span style={entryNum}>{entry.number}</span>
                    <span style={entryAmt}>₹{entry.amount}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {config.mode === "halfSangam" && (
          <section style={cleanCard}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
              <button type="button" style={sangamMode === "open" ? modeTabActive : modeTab} onClick={() => { setSangamMode("open"); setSangam((c) => ({ ...c, openDigit: "", closePana: "" })); }}>Pana + Digit</button>
              <button type="button" style={sangamMode === "close" ? modeTabActive : modeTab} onClick={() => { setSangamMode("close"); setSangam((c) => ({ ...c, openPana: "", closeDigit: "" })); }}>Digit + Pana</button>
            </div>
            {sangamMode === "open" ? (
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={cleanLabel}>Open Pana</div>
                  <input value={sangam.openPana} onChange={(e) => updateSangam("openPana", e.target.value, 3)} inputMode="numeric" placeholder="Enter 3-digit pana" style={cleanInput} />
                  {!!sangamErrors.openPana && <div style={errText}>{sangamErrors.openPana}</div>}
                </div>
                <div>
                  <div style={cleanLabel}>Close Digit</div>
                  <input value={sangam.closeDigit} onChange={(e) => updateSangam("closeDigit", e.target.value, 1)} inputMode="numeric" placeholder="Enter 1-digit" style={cleanInput} />
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={cleanLabel}>Open Digit</div>
                  <input value={sangam.openDigit} onChange={(e) => updateSangam("openDigit", e.target.value, 1)} inputMode="numeric" placeholder="Enter 1-digit" style={cleanInput} />
                </div>
                <div>
                  <div style={cleanLabel}>Close Pana</div>
                  <input value={sangam.closePana} onChange={(e) => updateSangam("closePana", e.target.value, 3)} inputMode="numeric" placeholder="Enter 3-digit pana" style={cleanInput} />
                  {!!sangamErrors.closePana && <div style={errText}>{sangamErrors.closePana}</div>}
                </div>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <div style={cleanLabel}>Enter Points</div>
              <input value={entryAmount} onChange={(e) => setEntryAmount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="₹5-₹10000" style={cleanInput} />
            </div>
            <button type="button" style={addBidBtn} onClick={addHalfSangamEntry}><Plus size={18} /> ADD BID</button>
            {!!entries.length && (
              <div style={entryListWrap}>
                {entries.map((entry, i) => <div key={i} style={entryListRow}><span style={entryNum}>{entry.number}</span><span style={entryAmt}>₹{entry.amount}</span></div>)}
              </div>
            )}
          </section>
        )}

        {config.mode === "fullSangam" && (
          <section style={cleanCard}>
            <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div>
                <div style={cleanLabel}>Open Pana</div>
                <input value={sangam.openPana} onChange={(e) => updateSangam("openPana", e.target.value, 3)} inputMode="numeric" placeholder="Enter 3-digit pana" style={cleanInput} />
                {!!sangamErrors.openPana && <div style={errText}>{sangamErrors.openPana}</div>}
              </div>
              <div>
                <div style={cleanLabel}>Close Pana</div>
                <input value={sangam.closePanaFull} onChange={(e) => updateSangam("closePanaFull", e.target.value, 3)} inputMode="numeric" placeholder="Enter 3-digit pana" style={cleanInput} />
                {!!sangamErrors.closePanaFull && <div style={errText}>{sangamErrors.closePanaFull}</div>}
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={cleanLabel}>Enter Points</div>
              <input value={entryAmount} onChange={(e) => setEntryAmount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="₹5-₹10000" style={cleanInput} />
            </div>
            <button type="button" style={addBidBtn} onClick={addFullSangamEntry}><Plus size={18} /> ADD BID</button>
            {!!entries.length && (
              <div style={entryListWrap}>
                {entries.map((entry, i) => <div key={i} style={entryListRow}><span style={entryNum}>{entry.number}</span><span style={entryAmt}>₹{entry.amount}</span></div>)}
              </div>
            )}
          </section>
        )}

        {config.mode === "motor" && (
          <section style={cleanCard}>
            <div style={cleanLabel}>{config.digitsLabel} <span style={{ color: "#94a3b8", fontWeight: 500 }}>{config.digitsHelper}</span></div>
            <input value={motorDigits} onChange={(e) => setMotorDigits(e.target.value.replace(/[^\d]/g, "").slice(0, 10))} inputMode="numeric" placeholder="Enter digits (e.g., 1234)" style={{ ...cleanInput, marginBottom: 6 }} />
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginBottom: 12 }}>Unique digits: {new Set((motorDigits || "").replace(/\D/g, "").split("").filter(Boolean)).size}/10</div>
            {!!generatedMotorEntries.length && (
              <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 12, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", marginBottom: 8 }}>Generated {variant === "dp_motor" ? "Double" : "Single"} Pattis ({generatedMotorEntries.length})</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 6 }}>
                  {generatedMotorEntries.map((n) => <div key={n} style={{ height: 32, borderRadius: 8, background: "#fff", border: "1px solid #e5e7eb", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, color: "#374151" }}>{n}</div>)}
                </div>
              </div>
            )}
            <div style={cleanLabel}>{config.amountLabel}</div>
            <input value={motorAmount} onChange={(e) => setMotorAmount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="₹5-₹10000" style={cleanInput} />
            <button type="button" style={addBidBtn} onClick={addMotorEntries}><Plus size={18} /> Add {generatedMotorEntries.length} {config.generatedLabel}</button>
            {!!entries.length && (
              <div style={entryListWrap}>
                {entries.map((entry, i) => <div key={i} style={entryListRow}><span style={entryNum}>{entry.number}</span><span style={entryAmt}>₹{entry.amount}</span></div>)}
              </div>
            )}
          </section>
        )}
      </main>

      <footer style={styles.footer}>
        <div style={styles.footerLeft}>
          <div style={styles.footerStat}>
            <span style={styles.footerStatLabel}>Bids</span>
            <strong style={styles.footerStatVal}>{selectedNumbers.length || (entries.length + (draftEntries.length > 0 ? 1 : 0)) || Object.values(gridAmounts).filter((v) => Number(v) > 0).length}</strong>
          </div>
          <div style={styles.footerStat}>
            <span style={styles.footerStatLabel}>Total</span>
            <strong style={{ ...styles.footerStatVal, color: effectiveTotal ? "#fbbf24" : "rgba(255,255,255,0.4)" }}>₹{effectiveTotal || "0"}</strong>
          </div>
        </div>
        <button
          type="button"
          disabled={!effectiveTotal || placing}
          style={{ ...styles.placeButton, opacity: !effectiveTotal || placing ? 0.5 : 1 }}
          onClick={placeBets}
        >
          {placing ? "Placing..." : "Place Bet"}
        </button>
      </footer>
      <MarketSheet
        open={marketSheetOpen}
        markets={marketOptions}
        selectedId={selectedMarket}
        onSelect={handleMarketChange}
        onClose={() => setMarketSheetOpen(false)}
      />

      {betSuccess && (
        <div style={betSuccessOverlay}>
          <div style={betSuccessCard}>
            <div style={betSuccessRing}>
              <svg viewBox="0 0 52 52" width="52" height="52">
                <circle cx="26" cy="26" r="24" fill="none" stroke="#22c55e" strokeWidth="3" />
                <path d="M14 26l8 8 16-16" fill="none" stroke="#22c55e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div style={betSuccessTitle}>Bet Placed!</div>
            <div style={betSuccessMarket}>{betSuccess.market}</div>
            <div style={betSuccessAmount}>₹{Number(betSuccess.total || 0).toFixed(0)}</div>
            <div style={betSuccessHint}>Redirecting to game select…</div>
          </div>
        </div>
      )}
    </div>
  );
}

function EntryList({ entries }) {
  return (
    <div style={styles.entryList}>
      {entries.map((entry, index) => (
        <div key={`${entry.number}-${index}`} style={styles.entryRow}>
          <span>{entry.number}</span>
          <span>{`₹${entry.amount}`}</span>
        </div>
      ))}
    </div>
  );
}

const SINGLE_PATTI_OPTIONS = [
  "127", "136", "145", "190", "235", "280", "370", "389", "460", "479", "569", "578",
  "128", "137", "146", "236", "245", "290", "380", "470", "489", "560", "579", "678",
  "129", "138", "147", "156", "237", "246", "345", "390", "480", "570", "589", "679",
  "120", "139", "148", "157", "238", "247", "256", "346", "490", "580", "670", "689",
  "130", "149", "158", "167", "239", "248", "257", "347", "356", "590", "680", "789",
  "140", "159", "168", "230", "249", "258", "267", "348", "357", "456", "690", "780",
  "123", "150", "169", "178", "240", "259", "268", "349", "358", "367", "457", "790",
  "124", "160", "278", "179", "250", "269", "340", "359", "368", "458", "467", "890",
  "125", "134", "170", "189", "260", "279", "350", "369", "468", "378", "459", "567",
  "126", "135", "180", "234", "270", "289", "360", "379", "450", "469", "478", "568",
];
const DOUBLE_PATTI_OPTIONS = [
  "118", "226", "244", "299", "334", "488", "550", "668", "677",
  "100", "119", "155", "227", "335", "344", "399", "588", "669",
  "110", "200", "228", "255", "336", "499", "660", "688", "778",
  "166", "229", "300", "337", "355", "445", "599", "779", "788",
  "112", "220", "266", "338", "400", "446", "455", "699", "770",
  "113", "122", "177", "339", "366", "447", "500", "799", "889", "555",
  "600", "114", "277", "330", "448", "466", "556", "880", "899",
  "115", "133", "188", "223", "377", "449", "557", "566", "700",
  "116", "224", "233", "288", "440", "477", "558", "800", "990",
  "117", "144", "199", "225", "388", "559", "577", "667", "900",
];
const TRIPLE_PATTI_OPTIONS = ["000", "111", "222", "333", "444", "555", "666", "777", "888", "999"];
const ALL_PANNA_OPTIONS = [...SINGLE_PATTI_OPTIONS, ...DOUBLE_PATTI_OPTIONS, ...TRIPLE_PATTI_OPTIONS];

function validatePattiValue(typePrefix, value) {
  const digits = String(value || "").split("");
  if (digits.length !== 3) return "";

  if (typePrefix === "sp") {
    return SINGLE_PATTI_OPTIONS.includes(value) ? "" : "Invalid single patti";
  }

  if (typePrefix === "dp") {
    return DOUBLE_PATTI_OPTIONS.includes(value) ? "" : "Invalid double patti";
  }

  const uniqueCount = new Set(digits).size;

  if (typePrefix === "tp" && uniqueCount !== 1) {
    return "Triple Patti only accepts all three digits same";
  }

  const canonicalError = validateGeneralPatti(value);
  if (canonicalError) return canonicalError;

  return "";
}

function validateGeneralPatti(value) {
  const raw = String(value || "");
  if (!/^\d{3}$/.test(raw)) return "";
  const normalized = raw.split("").sort().join("");
  if (raw !== normalized) {
    return `Invalid patti. Use ${normalized}`;
  }
  return "";
}

function validatePannaOption(value) {
  if (!/^\d{3}$/.test(String(value || ""))) return "";
  return ALL_PANNA_OPTIONS.includes(value) ? "" : "Invalid panna";
}

function getPannaSuggestions(value) {
  const query = String(value || "");
  if (!query) return [];
  return ALL_PANNA_OPTIONS.filter((option) => option.startsWith(query)).slice(0, 24);
}

function getSinglePattisForDigit(digit) {
  const target = Number(digit);
  return SINGLE_PATTI_OPTIONS.filter((option) =>
    option.split("").map(Number).reduce((sum, item) => sum + item, 0) % 10 === target
  );
}

function getDoublePattisForDigit(digit) {
  const target = Number(digit);
  return DOUBLE_PATTI_OPTIONS.filter((option) =>
    option.split("").map(Number).reduce((sum, item) => sum + item, 0) % 10 === target
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#fff", color: "#111827", fontFamily: "Inter, system-ui, sans-serif", paddingTop: 56, paddingBottom: 80, display: "flex", flexDirection: "column", alignItems: "center", boxSizing: "border-box" },
  topBar: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 50, height: 56, background: "#fff", display: "flex", alignItems: "center", gap: 10, padding: "0 14px", borderBottom: "1px solid #e5e7eb", boxSizing: "border-box" },
  circleBack: { width: 34, height: 34, borderRadius: "50%", border: "1px solid #e5e7eb", background: "#f8fafc", color: "#374151", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 },
  title: { margin: 0, fontSize: 13, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  main: { width: "100%", maxWidth: 640, margin: "0 auto", padding: "16px 14px 0", boxSizing: "border-box" },
  topRowFields: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 },
  outlineField: { height: 44, border: "1px solid #d1d5db", borderRadius: 8, display: "flex", alignItems: "center", gap: 8, padding: "0 12px", background: "#fff" },
  outlineFieldSelect: { height: 44, border: "1px solid #d1d5db", borderRadius: 8, position: "relative", background: "#fff" },
  sessionSelect: { width: "100%", height: "100%", border: 0, outline: 0, background: "transparent", appearance: "none", paddingLeft: 12, paddingRight: 30, fontSize: 14, color: "#374151", fontWeight: 500 },
  fieldText: { fontSize: 14, color: "#374151", fontWeight: 500 },
  selectorCard: { borderWidth: 1, borderStyle: "solid", borderRadius: 10, padding: 10, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)", boxSizing: "border-box" },
  selectorCardDesktop: { borderRadius: 18, padding: 18, gap: 16 },
  fieldLabel: { fontSize: 10, fontWeight: 700 },
  required: { color: "#ef4444" },
  selectWrap: { position: "relative", display: "block", marginTop: 8 },
  select: { width: "100%", height: 40, appearance: "none", borderWidth: 2, borderStyle: "solid", borderRadius: 10, background: "#fff", color: "#1f2937", padding: "0 30px 0 10px", fontSize: 10, fontWeight: 600, outline: "none" },
  selectButton: { display: "flex", alignItems: "center", textAlign: "left", minHeight: 42, lineHeight: 1.25, whiteSpace: "normal", cursor: "pointer" },
  selectIcon: { position: "absolute", right: 8, top: 11, pointerEvents: "none" },
  whiteCard: {
    marginTop: 10,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 10,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    boxSizing: "border-box",
  },
  secondaryLabel: {
    display: "block",
    color: "#475569",
    fontSize: 10,
    fontWeight: 700,
  },
  helperText: {
    color: "#6b7280",
    fontWeight: 600,
  },
  largeInputWrap: {
    position: "relative",
    marginTop: 8,
  },
  currency: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 18,
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 18,
    fontWeight: 700,
    pointerEvents: "none",
  },
  largeInput: {
    width: "100%",
    height: 56,
    border: "2px solid #bfdbfe",
    borderRadius: 14,
    background: "#fff",
    textAlign: "center",
    fontSize: 17,
    fontWeight: 700,
    color: "#111827",
    outline: "2px solid #111827",
    outlineOffset: "-6px",
    boxSizing: "border-box",
  },
  bulkTitle: {
    fontSize: 10,
    fontWeight: 800,
    gridColumn: "1 / -1",
    marginBottom: 4,
  },
  ankBanner: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: 4,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
  },
  numberGrid: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 8,
  },
  redBracketGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 6,
    marginTop: 10,
  },
  selectAllRed: {
    width: "100%",
    minHeight: 44,
    borderRadius: 12,
    border: "1.5px solid #c7d2fe",
    background: "#eef2ff",
    color: "#312e81",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 14px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    marginBottom: 10,
  },
  selectAllTick: {
    width: 22,
    height: 22,
    borderRadius: 4,
    border: "2px solid #9ca3af",
    background: "#fff",
    flexShrink: 0,
  },
  selectAllTickActive: {
    width: 22,
    height: 22,
    borderRadius: 4,
    border: "2px solid #16a34a",
    background: "#16a34a",
    boxShadow: "inset 0 0 0 3px #fff",
    flexShrink: 0,
  },
  jodiAmountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 12,
  },
  tripleAmountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
  },
  jodiCell: {
    display: "grid",
    gap: 6,
  },
  jodiNumber: {
    height: 38,
    borderRadius: 9,
    background: "#fef9c3",
    border: "1px solid #fde68a",
    display: "grid",
    placeItems: "center",
    color: "#78350f",
    fontSize: 13,
    fontWeight: 800,
  },
  jodiAmountInput: {
    width: "100%",
    height: 40,
    minWidth: 0,
    borderRadius: 11,
    border: "2px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  tripleNumber: {
    height: 40,
    borderRadius: 9,
    background: "#ffe7cf",
    border: "1px solid #fdba74",
    display: "grid",
    placeItems: "center",
    color: "#9a3412",
    fontSize: 14,
    fontWeight: 800,
  },
  numberButton: {
    height: 42,
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  sangamBox: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    background: "#f8fafc",
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  sangamBoxDisabled: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    background: "#f3f4f6",
    opacity: 0.65,
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    pointerEvents: "none",
  },
  orLine: {
    margin: "16px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
  smallInputWrap: {
    position: "relative",
    marginTop: 8,
  },
  smallCurrency: {
    position: "absolute",
    left: 13,
    top: 12,
    color: "#9ca3af",
    fontSize: 14,
    fontWeight: 700,
  },
  smallInput: {
    width: "100%",
    height: 44,
    marginTop: 6,
    border: "1px solid #d1d5db",
    borderRadius: 11,
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },
  smallInputDisabled: {
    width: "100%",
    height: 44,
    marginTop: 6,
    border: "1px solid #d1d5db",
    borderRadius: 11,
    background: "#e5e7eb",
    color: "#94a3b8",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
    cursor: "not-allowed",
  },
  autoDigit: {
    marginTop: 8,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    textAlign: "center",
  },
  suggestionList: {
    marginTop: 10,
    maxHeight: 180,
    overflowY: "auto",
    border: "1px solid #dbeafe",
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
  },
  suggestionButton: {
    width: "100%",
    minHeight: 34,
    border: 0,
    borderBottom: "1px solid #eff6ff",
    background: "#fff",
    color: "#1e3a8a",
    textAlign: "left",
    padding: "0 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  noResultText: {
    marginTop: 10,
    color: "#dc2626",
    fontSize: 10,
    fontWeight: 700,
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#4f46e5",
    fontSize: 11,
    fontWeight: 800,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },
  previewCard: {
    position: "relative",
    border: "1.5px solid #c7d2fe",
    borderRadius: 14,
    background: "#fff",
    minHeight: 58,
    display: "grid",
    placeItems: "center",
    padding: "10px 6px 8px",
    boxShadow: "0 2px 8px rgba(79,70,229,0.07)",
  },
  previewRemove: {
    position: "absolute",
    top: -7,
    right: -7,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: 0,
    background: "#ef4444",
    color: "#fff",
    fontSize: 14,
    lineHeight: 1,
    cursor: "pointer",
  },
  previewNumber: {
    color: "#4f46e5",
    fontSize: 17,
    fontWeight: 900,
    lineHeight: 1.1,
  },
  previewAmount: {
    color: "#f97316",
    fontSize: 10,
    fontWeight: 800,
    marginTop: 4,
  },
  errorText: {
    marginTop: 8,
    color: "#dc2626",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  fullInput: {
    width: "100%",
    height: 48,
    marginTop: 8,
    border: "2px solid #bfdbfe",
    borderRadius: 12,
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },
  fullInputCompact: {
    width: "100%",
    height: 42,
    marginTop: 8,
    border: "1px solid #d1d5db",
    borderRadius: 10,
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 14,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },
  motorCount: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 500,
    marginTop: 10,
  },
  generatedMotorCard: {
    marginTop: 16,
    border: "1px solid #fed7aa",
    borderRadius: 12,
    background: "#fff7ed",
    padding: 10,
  },
  generatedMotorTitle: {
    color: "#c2410c",
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 12,
  },
  generatedMotorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 8,
  },
  generatedMotorChip: {
    minHeight: 28,
    borderRadius: 8,
    background: "#ffedd5",
    color: "#c2410c",
    display: "grid",
    placeItems: "center",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 6px",
  },
  addButton: {
    width: "100%",
    height: 46,
    marginTop: 12,
    border: 0,
    borderRadius: 12,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
  },
  entryList: {
    marginTop: 12,
    display: "grid",
    gap: 8,
  },
  entryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 10px",
    borderRadius: 10,
    background: "#f8fafc",
    color: "#334155",
    fontSize: 12,
    fontWeight: 600,
  },
  footer: { position: "fixed", left: 0, right: 0, bottom: 0, height: 68, background: "linear-gradient(135deg,#1e1b4b,#312e81)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderTop: "1px solid rgba(255,255,255,0.08)", boxSizing: "border-box", boxShadow: "0 -4px 20px rgba(30,27,75,0.35)" },
  footerLeft: { display: "flex", gap: 22 },
  footerStat: { display: "flex", flexDirection: "column", gap: 2 },
  footerStatLabel: { fontSize: 10, color: "rgba(255,255,255,0.5)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" },
  footerStatVal: { fontSize: 16, fontWeight: 900, color: "#fff" },
  placeButton: { height: 44, padding: "0 26px", border: 0, borderRadius: 12, background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", fontSize: 15, fontWeight: 900, cursor: "pointer", boxShadow: "0 4px 16px rgba(249,115,22,0.4)", letterSpacing: "0.02em" },
  totalLabel: { color: "#6b7280", fontSize: 10, fontWeight: 400 },
  totalAmount: { fontSize: 17, fontWeight: 900, marginTop: 2 },
};

const cleanCard     = { marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "16px 14px", boxSizing: "border-box" };
const cleanLabel    = { fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 };
const cleanInput    = { width: "100%", height: 46, border: "1px solid #d1d5db", borderRadius: 10, padding: "0 14px", boxSizing: "border-box", fontSize: 15, fontWeight: 600, color: "#111827", outline: "none", background: "#fff", display: "block" };
const addBidBtn     = { width: "100%", height: 46, marginTop: 14, border: 0, borderRadius: 12, background: "#312e81", color: "#fff", fontWeight: 800, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" };
const entryListWrap = { marginTop: 12, display: "grid", gap: 6 };
const entryListRow  = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10 };
const entryNum      = { fontSize: 14, fontWeight: 700, color: "#1e1b4b" };
const entryAmt      = { fontSize: 14, fontWeight: 800, color: "#312e81" };
const autoDigitBadge= { marginTop: 4, fontSize: 11, fontWeight: 700, color: "#4f46e5", background: "#eef2ff", borderRadius: 6, padding: "3px 8px", display: "inline-block" };
const errText       = { marginTop: 4, fontSize: 11, color: "#dc2626", fontWeight: 700 };
const modeTab       = { height: 38, border: "1.5px solid #e5e7eb", borderRadius: 10, background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: 13, cursor: "pointer" };
const modeTabActive = { ...modeTab, background: "#312e81", color: "#fff", border: "1.5px solid #312e81" };

const betSuccessOverlay = { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const betSuccessCard = { background: "#fff", borderRadius: 28, padding: "36px 28px 28px", textAlign: "center", width: "min(340px,100%)", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, animation: "popIn 0.3s cubic-bezier(0.34,1.56,0.64,1)" };
const betSuccessRing = { width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg,#dcfce7,#bbf7d0)", display: "grid", placeItems: "center", marginBottom: 4 };
const betSuccessTitle = { fontSize: 24, fontWeight: 900, color: "#15803d", letterSpacing: "-0.02em" };
const betSuccessMarket = { fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" };
const betSuccessAmount = { fontSize: 32, fontWeight: 900, color: "#1e1b4b", letterSpacing: "-0.02em", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" };
const betSuccessHint = { fontSize: 12, color: "#94a3b8", fontWeight: 500, marginTop: 8 };

export default GameBetPage;

